import { Component, computed, inject, input, OnChanges, output, signal, SimpleChanges, viewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertController, IonInputOtp } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';

import { CountriesService, CountryPhoneConfig } from '@smartcharge/shared/index';

import { AuthMethod, AuthViewState, LoginConfig } from '../auth.component';
import { UserService } from '../../../services';
import { ToastService } from '../../../shared/services/toast/toast.service';
import { ChargingService } from '../../../services/charging/charging.service';

@Component({
  selector: 'sc-login',
  standalone: false,
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss'
})
export class LoginComponent implements OnChanges {
  readonly emailForm = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl('', [Validators.required])
  });

  readonly phoneNumberForm = new FormGroup({
    country: new FormControl<CountryPhoneConfig | null>(null, [Validators.required]),
    phoneNumber: new FormControl('', [Validators.required])
  });

  readonly codeControl = new FormControl('', [Validators.required, Validators.pattern(/^\d{6}$/)]);

  readonly config = input<Partial<LoginConfig>>();

  readonly authMethod = signal<AuthMethod>('phone');
  readonly canLoginWithEmail = signal<boolean>(true);
  readonly codeSent = signal<boolean>(false);
  readonly countries = signal<CountryPhoneConfig[]>([]);
  readonly sendCodeCooldown = signal<number>(60);
  readonly sending = signal<boolean>(false);
  readonly showPassword = signal<boolean>(false);

  readonly close = output<boolean>();
  readonly switchView = output<AuthViewState>();

  readonly otpInput = viewChild<IonInputOtp>('otpInput');

  readonly disableResetCode = computed(() => {
    return this.sendCodeCooldown() > 0;
  });

  private readonly alertController = inject(AlertController);
  private readonly chargingService = inject(ChargingService);
  private readonly countriesService = inject(CountriesService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly userService = inject(UserService);

  constructor() {
    // Check if user have any ongoing session before proceesing
    this.checkOngoingChargingSessions();

    this.countriesService.getCountriesWithSMSSupport().subscribe((countries) => {
      this.countries.set(countries);

      if (countries.length) {
        this.phoneNumberForm.controls.country.setValue(countries[0]);
      }
    });

    this.codeControl.valueChanges.subscribe(() => {
      if (this.codeControl.valid) {
        this.verify();
      }
    });

    if (this.route.snapshot.root.firstChild?.paramMap.has('validationKEY')) {
      this.canLoginWithEmail.set(false);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('config' in changes && this.config()) {
      const { method } = this.config()!;

      if (method) {
        this.setAuthMethod(method);
      }
    }
  }

  countryChanged(): void {
    this.phoneNumberForm.controls.phoneNumber.setValue('');
  }

  loginEmail() {
    const { email, password } = this.emailForm.value;

    if (!email || !password) {
      return;
    }

    this.userService
      .login(email.toLowerCase(), password)
      .then(() => {
        this.close.emit(true);

        this.router.navigate(['stations'], { replaceUrl: true });
      })
      .catch((error) => {
        this.toastService.error(error);
      });
  }

  obfuscatedPhoneNumber(): string {
    const { country, phoneNumber } = this.phoneNumberForm.value;

    if (!country || !phoneNumber) {
      return '';
    }

    return `+${country.DialingCode}***${phoneNumber.slice(-3)}`;
  }

  sendCode(): void {
    if (this.phoneNumberForm.invalid) {
      return;
    }

    const { country, phoneNumber } = this.phoneNumberForm.value;

    if (!country || !phoneNumber) {
      return;
    }

    this.userService
      .smsCodeRequest({
        CountryID: country.CountryId,
        Phone: phoneNumber
      })
      .subscribe({
        next: () => {},
        error: () => {
          this.toastService.error(this.translateService.instant('ERROR.DEFAULT_ERROR'));
        }
      });

    this.codeSent.set(true);

    this.sendCodeCooldown.set(60);

    const interval = setInterval(() => {
      this.sendCodeCooldown.update((value) => {
        if (value > 0) {
          return value - 1;
        } else {
          clearInterval(interval);
          return 0;
        }
      });
    }, 1000);
  }

  setAuthMethod(method: AuthMethod) {
    this.authMethod.set(method);
  }

  toggleShowPassword() {
    this.showPassword.update((show) => !show);
  }

  verify(): void {
    const { country, phoneNumber } = this.phoneNumberForm.value;
    const code = this.codeControl.value;

    if (!country || !phoneNumber || !code) {
      return;
    }

    this.sending.set(true);

    this.userService
      .smsAuth({
        Code: code,
        CountryID: country.CountryId,
        Phone: phoneNumber
      })
      .then((result) => {
        this.sending.set(false);

        if (result.DoesUserNeedToCompleteRegistration) {
          this.switchView.emit({ view: 'register', config: { loginRes: result } });
        } else {
          this.close.emit(true);
        }
      })
      .catch((error) => {
        this.sending.set(false);
        this.toastService.error(error);

        this.codeControl.reset();
        this.otpInput()?.setFocus(0);
      });
  }

  private async checkOngoingChargingSessions(): Promise<void> {
    try {
      const activeSessions = await this.chargingService.getSessionsActiveIDs();

      if (activeSessions.length > 0) {
        const alert = await this.alertController.create({
          header: this.translateService.instant('AUTH.ONGOING_SESSION_ALERT.TITLE'),
          message: this.translateService.instant('AUTH.ONGOING_SESSION_ALERT.MESSAGE'),
          buttons: [
            {
              text: this.translateService.instant('APP.CANCEL'),
              role: 'cancel'
            }
          ]
        });

        alert.onDidDismiss().then(() => {
          this.close.emit(false);
        });

        await alert.present();
      }
    } catch (_e) {}
  }
}
